import CheckIcon from '@mui/icons-material/Check';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import { IconButton, Stack } from '@mui/material';
import { useEffect, useRef, useState } from 'react';
import theme from '../../../../../../../Theme';
import Typography from '../../../../../../../components/Typography/Typography';
import LangEnum from '../../../../../../../lang/LangEnum';
import snackbarStore from '../../../../../../../stores/Snackbar.store';
import { useTranslation } from '../../../../../../translation/TranslationContext';

interface OrderPaymentCopyLinkContentProps {
  paymentUrl: string;
}

const FIVE_SECONDS_TO_RESET_STATE = 5000;

const OrderPaymentCopyLinkContent = ({ paymentUrl }: OrderPaymentCopyLinkContentProps) => {
  const { translate } = useTranslation();

  const [isCopied, setIsCopied] = useState(false);
  const timeoutRef = useRef<NodeJS.Timeout>();

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(paymentUrl);
      setIsCopied(true);
      timeoutRef.current = setTimeout(() => setIsCopied(false), FIVE_SECONDS_TO_RESET_STATE);
    } catch (_) {
      snackbarStore.handleMessage({
        type: 'error',
        message: translate(LangEnum.SNACKBAR_ERROR_OCCURRED_TRY_AGAIN),
      });
    }
  };

  useEffect(() => {
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, []);

  return (
    <Stack spacing={2}>
      <Typography color="text.secondary">{translate(LangEnum.PAYMENT_LINK_COPY_LINK)}</Typography>
      <Stack
        direction="row"
        sx={{
          alignItems: 'center',
          justifyContent: 'space-between',
          padding: '8px',
          borderRadius: theme.shape.borderRadius,
          border: `1px solid ${theme.palette.divider}`,
          '& .copy-button': { opacity: 0 },
          '&:hover .copy-button': { opacity: 1 },
        }}
      >
        <Typography color="text.secondary">{paymentUrl}</Typography>

        <IconButton className="copy-button" onClick={handleCopy} size="small">
          {isCopied ? <CheckIcon fontSize="small" /> : <ContentCopyIcon fontSize="small" />}
        </IconButton>
      </Stack>
    </Stack>
  );
};

export default OrderPaymentCopyLinkContent;
